import type { ReactNode } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';
import type { PopoverOrigin, IconButtonProps } from '@mui/material';
import type { AccountMenuLegalLink } from './legal-links';

import { useId, useState } from 'react';

import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import BalanceIcon from '@mui/icons-material/BalanceOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import { Menu, Divider, Typography, IconButton } from '@mui/material';

import { HoverMenuItem } from '../hover-menu';
import { AccountAvatar } from '../account-avatar/account-avatar';
import { getInitials } from '../account-avatar/account-avatar-utils';
import { AccountMenuItem, accountMenuIconSx, accountMenuItemSx } from './account-menu-item';

// ----------------------------------------------------------------------

export type AccountMenuProps = {
  name?: string | null;
  email?: string | null;
  avatarSrc?: string;
  /** Initials stand in for a missing photo. Off keeps the MUI person glyph. */
  showInitials?: boolean;
  triggerProps?: Omit<IconButtonProps, 'onClick' | 'children'> & { 'data-testid'?: string };
  /** Extra rows above Legal. Call `close` to shut the menu. */
  items?: (close: () => void) => ReactNode[];
  legalLabel?: string;
  legalLinks?: AccountMenuLegalLink[];
  signOutLabel: string;
  onSignOut: () => void;
  signOutProps?: { 'data-testid'?: string };
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  paperSx?: SxProps<Theme>;
};

/** The avatar button and its menu: extra rows, Legal, then sign-out. */
export function AccountMenu({
  name,
  email,
  avatarSrc,
  showInitials = true,
  triggerProps,
  items,
  legalLabel,
  legalLinks = [],
  signOutLabel,
  onSignOut,
  signOutProps,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  paperSx,
}: AccountMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // React ids hold colons, which jsdom cannot use in a selector.
  const menuId = `account-menu-${useId().replace(/[^\w-]/g, '')}`;
  const open = Boolean(anchor);
  const close = () => setAnchor(null);

  const initials = showInitials ? getInitials(name ?? undefined, email ?? '') : '';
  const extraItems = items?.(close) ?? [];
  const showLegal = Boolean(legalLabel) && legalLinks.length > 0;
  const { sx: triggerSx, ...trigger } = triggerProps ?? {};

  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <>
      <IconButton
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        {...trigger}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={[{ p: 0 }, ...(Array.isArray(triggerSx) ? triggerSx : [triggerSx])]}
      >
        <AccountAvatar src={avatarSrc} alt={name || email || ''}>
          {initials || undefined}
        </AccountAvatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: [
              (theme) => ({
                // Sized to content, so long localized labels are not cut off.
                minWidth: 164,
                maxWidth: 280,
                p: 0.25,
                borderRadius: 1,
                bgcolor: 'container.low',
                boxShadow: theme.customShadows.dropdown,
                mt: theme.spacing(1),
              }),
              ...(Array.isArray(paperSx) ? paperSx : [paperSx]),
            ],
          },
          list: {
            id: menuId,
            sx: { p: 0, display: 'flex', flexDirection: 'column' },
          },
        }}
      >
        {extraItems}

        {showLegal && (
          <HoverMenuItem
            submenu={legalLinks.map((link) => (
              <AccountMenuItem
                key={link.key}
                label={link.label}
                icon={<OpenInNewIcon fontSize="small" sx={accountMenuIconSx} />}
                onClick={() => openLink(link.url)}
              />
            ))}
            submenuPosition="left"
            submenuPaperSx={{ minWidth: 180, bgcolor: 'container.low' }}
            sx={accountMenuItemSx}
            hideChevron
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {legalLabel}
            </Typography>
            <BalanceIcon sx={accountMenuIconSx} />
          </HoverMenuItem>
        )}

        {(extraItems.length > 0 || showLegal) && <Divider sx={{ my: 0 }} />}

        <AccountMenuItem
          {...signOutProps}
          label={signOutLabel}
          icon={<LogoutIcon sx={accountMenuIconSx} />}
          onClick={() => {
            close();
            onSignOut();
          }}
        />
      </Menu>
    </>
  );
}
